import {
  EquipmentModelType,
  EquipmentSearchFields,
  QueryEquipmentQuery,
  QueryEquipmentQueryVariables,
  SearchType,
} from '__generated__/graphql';
import { EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { useCallback } from 'react';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { logError } from 'utils/testUtils';

export type EquipmentPickerItem = NonNullArrayField<QueryEquipmentQuery['equipment']>;

export interface UseEquipmentPickerDataOptions {
  filteredIds?: string[];
  filterAddOns?: boolean;
}

export function useEquipmentPickerData({ filteredIds, filterAddOns = true }: UseEquipmentPickerDataOptions = {}) {
  const graphqlClient = useGraphQLClient();

  const fetchPage = useCallback(
    async (after: string | null, first: number, searchDebounce: string): Promise<EquipmentPickerItem[]> => {
      const result = await graphqlClient
        .request<QueryEquipmentQuery, QueryEquipmentQueryVariables>({
          document: EQUIPMENT_QUERY,
          variables: {
            after,
            first,
            filter: {
              deletedOn: { isNull: true },
              search: searchDebounce
                ? {
                    type: SearchType.Contains,
                    fields: [
                      EquipmentSearchFields.EquipmentName,
                      EquipmentSearchFields.ModelName,
                      EquipmentSearchFields.MakeName,
                    ],
                    value: searchDebounce,
                  }
                : undefined,
              model: {
                type: filterAddOns ? { doesNotContain: [EquipmentModelType.Attachment] } : undefined,
              },
              id: filteredIds?.length ? { doesNotContain: filteredIds } : undefined,
            },
          },
        })
        .catch((e) => {
          logError(e);
          throw e;
        });

      return result.equipment ?? [];
    },
    [graphqlClient, filterAddOns, filteredIds]
  );

  const fetchSelected = useCallback(
    async (ids: string[]): Promise<EquipmentPickerItem[]> => {
      const result = await graphqlClient.request<QueryEquipmentQuery, QueryEquipmentQueryVariables>({
        document: EQUIPMENT_QUERY,
        variables: { filter: { id: { contains: ids } } },
      });

      return result.equipment ?? [];
    },
    [graphqlClient]
  );

  return { fetchPage, fetchSelected };
}
